import LanguageDetector from "i18next-browser-languagedetector";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGE_CODES } from "./languages";

// Which language the UI shows, per the company settings defaultLanguage and
// languageDetectionEnabled (WP4). Detection on: today's detector (?lng=,
// i18nextLng, browser) with the company default only as the fallback.
// Detection off: the user's own switcher choice (nx.lang.explicit), else the
// company default; ?lng=, i18nextLng and the browser are not read, and
// i18nextLng is never written.

export const EXPLICIT_LANGUAGE_KEY = "nx.lang.explicit";

// Today's detector settings, used only while detection is on.
export const DETECTION_OPTIONS = Object.freeze({
  order: ["querystring", "localStorage", "navigator"],
  lookupQuerystring: "lng",
  caches: ["localStorage"],
});

const isSupported = (code) => SUPPORTED_LANGUAGE_CODES.includes(code);

/** Explicit reading of the two settings; a missing field is conservative. */
export const readLanguagePolicy = (source) => ({
  detectionEnabled: source?.languageDetectionEnabled === true,
  defaultLanguage: isSupported(source?.defaultLanguage)
    ? source.defaultLanguage
    : DEFAULT_LANGUAGE,
});

// English without detection: before any session or host policy is known,
// and when the pre-login defaults cannot be read.
export const CONSERVATIVE_LANGUAGE_POLICY = Object.freeze(
  readLanguagePolicy(undefined),
);

const samePolicy = (a, b) =>
  a.detectionEnabled === b.detectionEnabled &&
  a.defaultLanguage === b.defaultLanguage;

const getStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage || null : null;
  } catch {
    return null;
  }
};

export const readExplicitLanguage = () => {
  try {
    const value = getStorage()?.getItem(EXPLICIT_LANGUAGE_KEY);
    return isSupported(value) ? value : null;
  } catch {
    return null;
  }
};

const writeExplicitLanguage = (code) => {
  try {
    getStorage()?.setItem(EXPLICIT_LANGUAGE_KEY, code);
  } catch {
    // Storage unavailable: the choice lasts for this page only.
  }
};

/**
 * One controller per i18next instance. Its detector plugin replaces the bare
 * browser detector; every language change goes through one serialized chain
 * with an explicit code, so a superseded load can never commit.
 */
export const createLanguageController = () => {
  let i18n = null;
  let browserDetector = null;
  let matcher = null;
  let activePolicy = CONSERVATIVE_LANGUAGE_POLICY;
  // Set only while a policy application commits its fallback language, so
  // the fallback is never cached in i18nextLng (a switcher choice always is).
  let suppressCache = false;
  let chain = Promise.resolve();

  // i18next's own matching (exact, script, language part) with no fallback,
  // so "nothing supported" is told apart from a match on English.
  const matchSupported = (codes) => {
    if (!matcher || !Array.isArray(codes) || codes.length === 0) return null;
    return matcher.getBestMatchFromCodes(codes) || null;
  };

  const resolve = (policy) => {
    if (policy.detectionEnabled) {
      const detected = matchSupported(browserDetector?.detect());
      if (detected) return { lng: detected, source: "detected" };
      return { lng: policy.defaultLanguage, source: "default" };
    }
    const explicit = readExplicitLanguage();
    if (explicit) return { lng: explicit, source: "explicit" };
    return { lng: policy.defaultLanguage, source: "default" };
  };

  const detector = {
    type: "languageDetector",
    init(services, detectorOptions) {
      browserDetector = new LanguageDetector(services, detectorOptions);
      const utils = services.languageUtils;
      // LanguageUtil is not exported by i18next; reach it through the
      // instance's own utils (a test guards this).
      matcher = new utils.constructor({ ...utils.options, fallbackLng: false });
    },
    // i18next calls this only for an argument-less changeLanguage, which
    // this controller never makes; kept consistent with the active policy.
    detect() {
      return resolve(activePolicy).lng;
    },
    cacheUserLanguage(lng) {
      if (!activePolicy.detectionEnabled || suppressCache) return;
      browserDetector?.cacheUserLanguage(lng);
    },
  };

  const run = (task) => {
    const next = chain.then(task).catch((error) => {
      console.error("Language change failed:", error);
    });
    chain = next;
    return next;
  };

  const change = async (lng, { suppress }) => {
    suppressCache = suppress;
    try {
      await i18n?.changeLanguage(lng);
    } finally {
      suppressCache = false;
    }
  };

  return {
    detector,
    attach(instance) {
      i18n = instance;
    },
    /** The language before any policy: the switcher choice, else English. */
    initialLanguage: () => readExplicitLanguage() || DEFAULT_LANGUAGE,
    getActivePolicy: () => activePolicy,
    /** Applies a policy if it differs from the active one. */
    applyPolicy: (policy) =>
      run(async () => {
        if (!policy || samePolicy(policy, activePolicy)) return;
        activePolicy = policy;
        const { lng, source } = resolve(policy);
        await change(lng, { suppress: source === "default" });
      }),
    /** The language switcher: the only writer of nx.lang.explicit. */
    chooseLanguage: (code) =>
      run(async () => {
        if (!isSupported(code)) return;
        writeExplicitLanguage(code);
        await change(code, { suppress: false });
      }),
    whenSettled: () => chain,
  };
};

/**
 * GET /ui-defaults (the host's pre-login language defaults). Any failure
 * gives the conservative policy; nothing is kept, so the next load retries.
 */
export const fetchPreLoginLanguagePolicy = async (get) => {
  try {
    const response = await get("/ui-defaults", { timeout: 5000 });
    const body = response?.data;
    if (body?.status !== "success" || !body.data) {
      return CONSERVATIVE_LANGUAGE_POLICY;
    }
    return readLanguagePolicy(body.data);
  } catch {
    return CONSERVATIVE_LANGUAGE_POLICY;
  }
};
